import React from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { DialogFooter } from '../components/ui/dialog';
import { Loader2, Save } from 'lucide-react';
import { useCanSeeRevenue } from '../lib/perms';

/**
 * WorkbenchEditCandidateForm - Extracted form component to prevent scroll issues
 * Used in the Recruiter Workbench for editing candidate information
 */
const WorkbenchEditCandidateForm = ({
  formData,
  setFormData,
  onSubmit,
  onCancel,
  formLoading = false,
  disabledPhone = '',
}) => {
  const canSeeRevenue = useCanSeeRevenue();
  return (
    <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Name *</Label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            placeholder="Candidate name"
            data-testid="workbench-candidate-name"
          />
        </div>
        <div className="space-y-2">
          <Label>Email</Label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            placeholder="Email address"
            data-testid="workbench-candidate-email"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Phone Number</Label>
          <Input
            value={disabledPhone || 'N/A'}
            disabled
            className="bg-slate-100 cursor-not-allowed"
          />
          <p className="text-xs text-slate-500">Phone number cannot be modified</p>
        </div>
        <div className="space-y-2">
          <Label>Experience (Years)</Label>
          <Input
            type="number"
            value={formData.experience_years}
            onChange={(e) => setFormData({...formData, experience_years: e.target.value})}
            placeholder="Years of experience"
            min="0"
            step="0.5"
            data-testid="workbench-candidate-exp"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Current Company</Label>
          <Input
            value={formData.current_company}
            onChange={(e) => setFormData({...formData, current_company: e.target.value})}
            placeholder="Current company"
            data-testid="workbench-candidate-company"
          />
        </div>
        <div className="space-y-2">
          <Label>Current Designation</Label>
          <Input
            value={formData.current_designation}
            onChange={(e) => setFormData({...formData, current_designation: e.target.value})}
            placeholder="Current job title"
            data-testid="workbench-candidate-designation"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Current Location</Label>
          <Input
            value={formData.current_location}
            onChange={(e) => setFormData({...formData, current_location: e.target.value})}
            placeholder="City, State"
            data-testid="workbench-candidate-location"
          />
        </div>
        <div className="space-y-2">
          <Label>Education</Label>
          <Input
            value={formData.education}
            onChange={(e) => setFormData({...formData, education: e.target.value})}
            placeholder="Education details"
            data-testid="workbench-candidate-education"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {canSeeRevenue && (
        <div className="space-y-2">
          <Label>Current CTC (LPA)</Label>
          <Input
            type="number"
            value={formData.current_ctc}
            onChange={(e) => setFormData({...formData, current_ctc: e.target.value})}
            placeholder="e.g., 12"
            min="0"
            step="0.5"
            data-testid="workbench-candidate-current-ctc"
          />
        </div>
        )}
        {canSeeRevenue && (
        <div className="space-y-2">
          <Label>Expected CTC (LPA)</Label>
          <Input
            type="number"
            value={formData.expected_ctc}
            onChange={(e) => setFormData({...formData, expected_ctc: e.target.value})}
            placeholder="e.g., 15"
            min="0"
            step="0.5"
            data-testid="workbench-candidate-expected-ctc"
          />
        </div>
        )}
        <div className="space-y-2">
          <Label>Notice Period (Days)</Label>
          <Input
            type="number"
            value={formData.notice_period}
            onChange={(e) => setFormData({...formData, notice_period: e.target.value})}
            placeholder="e.g., 30"
            min="0"
            data-testid="workbench-candidate-notice"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Skills (comma-separated)</Label>
        <Input
          value={formData.skills}
          onChange={(e) => setFormData({...formData, skills: e.target.value})}
          placeholder="React, Node.js, Python"
          data-testid="workbench-candidate-skills"
        />
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          disabled={formLoading || !formData.name}
          className="bg-teal-700 hover:bg-teal-800"
          data-testid="workbench-save-candidate-btn"
        >
          {formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save Changes
        </Button>
      </DialogFooter>
    </div>
  );
};

export default WorkbenchEditCandidateForm;
